import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { LOGO_URL } from "../lib/api";
import { PARTNER_LETTER_LANGUAGES, PARTNER_LETTERS } from "../data/partnerWelcomeLetter";

const languageNames = { en: "English", ta: "தமிழ்", ml: "മലയാളം", te: "తెలుగు", kn: "ಕನ್ನಡ", hi: "हिन्दी" };

function dateText(value, language) {
  const date = value ? new Date(value) : new Date();
  const locale = { en: "en-IN", ta: "ta-IN", ml: "ml-IN", te: "te-IN", kn: "kn-IN", hi: "hi-IN" }[language] || "en-IN";
  return date.toLocaleDateString(locale, { year: "numeric", month: "long", day: "numeric" });
}

export default function PartnerWelcomeLetter({ member, acceptedAt, acceptedLanguage, required = false, onAccept, onClose, accepting = false, readOnly = false }) {
  const [language, setLanguage] = useState(required ? "en" : acceptedLanguage || "en");
  const [readToEnd, setReadToEnd] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const t = PARTNER_LETTERS[language] || PARTNER_LETTERS.en;
  const activated = dateText(member?.activated_at, language);
  const acceptedDate = acceptedAt ? new Date(acceptedAt).toLocaleString(
    { en: "en-IN", ta: "ta-IN", ml: "ml-IN", te: "te-IN", kn: "kn-IN", hi: "hi-IN" }[language] || "en-IN",
    { dateStyle: "long", timeStyle: "short" },
  ) : dateText(null, language);

  useEffect(() => {
    const clearPrintMode = () => {
      document.body.classList.remove("print-partner-welcome");
      window.removeEventListener("afterprint", clearPrintMode);
    };
    return clearPrintMode;
  }, []);

  const changeLanguage = (event) => {
    setLanguage(event.target.value);
    if (required) {
      setReadToEnd(false);
      setConfirmed(false);
      document.getElementById("partner-welcome-letter")?.scrollTo({ top: 0 });
    }
  };

  const downloadPdf = () => {
    const oldTitle = document.title;
    document.title = `VEARN Partner Welcome Letter - ${member?.name || "Partner"} - ${member?.partner_id || ""}`;
    document.body.classList.add("print-partner-welcome");
    window.addEventListener("afterprint", () => {
      document.body.classList.remove("print-partner-welcome");
      document.title = oldTitle;
    }, { once: true });
    window.print();
  };

  return (
    <>
      <style>{`
        @media print {
          @page { size: A4; margin: 16mm; }
          html, body { background: #fff !important; }
          body.print-partner-welcome * { visibility: hidden !important; }
          body.print-partner-welcome #partner-welcome-letter,
          body.print-partner-welcome #partner-welcome-letter * { visibility: visible !important; }
          body.print-partner-welcome .partner-welcome-overlay { position: absolute !important; inset: 0 !important; display: block !important; overflow: visible !important; padding: 0 !important; background: #fff !important; }
          body.print-partner-welcome #partner-welcome-letter { position: absolute !important; inset: 0 !important; width: 100% !important; max-width: none !important; max-height: none !important; overflow: visible !important; padding: 0 !important; border: 0 !important; border-radius: 0 !important; box-shadow: none !important; color: #172033 !important; }
          body.print-partner-welcome .partner-welcome-actions, body.print-partner-welcome .partner-welcome-accept-controls { display: none !important; }
          body.print-partner-welcome .partner-letter-section { break-inside: avoid; }
          body.print-partner-welcome .partner-letter-paper { padding: 0 !important; background: #fff !important; }
        }
      `}</style>
      <div className="partner-welcome-overlay fixed inset-0 z-[200] flex items-start justify-center overflow-y-auto bg-slate-950/80 p-3 backdrop-blur-sm sm:p-6" data-testid="partner-welcome-overlay">
        <article id="partner-welcome-letter" className="w-full max-w-4xl max-h-[calc(100vh-24px)] overflow-y-auto rounded-2xl border border-slate-200 bg-white text-slate-800 shadow-2xl sm:max-h-[calc(100vh-48px)]" onScroll={(event) => {
          const el = event.currentTarget;
          if (el.scrollHeight - el.scrollTop - el.clientHeight < 24) setReadToEnd(true);
        }}>
          <div className="partner-welcome-actions sticky top-0 z-10 flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-white/95 px-5 py-3 backdrop-blur sm:px-8">
            <label className="flex items-center gap-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
              <span>{t.languageLabel || "Language"}</span>
              <select data-testid="welcome-letter-language" value={language} onChange={changeLanguage} className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold normal-case tracking-normal text-slate-800 outline-none focus:ring-2 focus:ring-blue-500">
                {PARTNER_LETTER_LANGUAGES.map((code) => <option key={code} value={code}>{languageNames[code]}</option>)}
              </select>
            </label>
            <div className="flex items-center gap-2">
              <button type="button" data-testid="welcome-letter-pdf" onClick={downloadPdf} className="flex h-10 items-center gap-2 rounded-full bg-slate-900 px-4 text-sm font-semibold text-white hover:bg-slate-700">
                <Download className="h-4 w-4" /> {t.downloadButton}
              </button>
              {!required && <button type="button" aria-label={t.closeButton} onClick={onClose} className="grid h-10 w-10 place-items-center rounded-full text-slate-500 hover:bg-slate-100"><X className="h-5 w-5" /></button>}
            </div>
          </div>

          <div className="partner-letter-paper px-6 py-8 sm:px-14 sm:py-12" lang={language}>
            {required && readOnly && <div role="status" className="mb-6 rounded-xl border border-amber-300 bg-amber-50 px-5 py-4 text-sm leading-6 text-amber-950">
              This is an admin read-only preview. To record your acceptance, return to Admin, sign out, and sign in with this Partner’s own account.
            </div>}
            <header className="border-b-2 border-blue-900 pb-7 text-center">
              <img src={LOGO_URL} alt="VEARN E-Commerce" className="mx-auto mb-5 h-auto w-44 object-contain" />
              <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-amber-600">VEARN · PARTNER RELATIONS</p>
              <h1 className="mt-3 font-serif text-2xl font-bold tracking-wide text-blue-950 sm:text-3xl">{t.title}</h1>
              <p className="mt-2 font-serif text-lg text-slate-600">{t.welcome}</p>
            </header>

            <section className="my-7 grid grid-cols-1 gap-x-8 gap-y-3 rounded-xl border border-slate-200 bg-slate-50 p-5 text-sm sm:grid-cols-2">
              <p><span className="font-semibold text-slate-500">{t.dateLabel}:</span> <span className="font-medium">{activated}</span></p>
              <p><span className="font-semibold text-slate-500">{t.nameLabel}:</span> <span className="font-semibold text-blue-950">{member?.name || "—"}</span></p>
              <p><span className="font-semibold text-slate-500">{t.idLabel}:</span> <span className="font-mono font-bold text-blue-800">{member?.partner_id || "—"}</span></p>
              <p className="sm:col-span-2"><span className="font-semibold text-slate-500">{t.subjectLabel}:</span> {t.subject}</p>
            </section>

            <p className="font-serif text-base font-semibold text-slate-800">{t.greeting}</p>
            <p className="mt-3 text-sm leading-7 text-slate-700">{t.introduction}</p>

            <div className="mt-7 space-y-6">
              {t.sections.map((section) => (
                <section key={section.title} className="partner-letter-section">
                  <h2 className="border-l-4 border-amber-500 pl-3 font-serif text-lg font-bold text-blue-950">{section.title}</h2>
                  {section.paragraphs.map((paragraph, index) => <p key={index} className="mt-2 text-sm leading-7 text-slate-700">{paragraph}</p>)}
                  {section.bullets && <ul className="mt-2 list-disc space-y-1.5 pl-6 text-sm leading-6 text-slate-700">{section.bullets.map((item, index) => <li key={index}>{item}</li>)}</ul>}
                </section>
              ))}
            </div>

            <section className="partner-letter-section mt-8 rounded-xl border border-amber-200 bg-amber-50/70 p-5 sm:p-6">
              <h2 className="font-serif text-xl font-bold text-blue-950">{t.closingTitle}</h2>
              <p className="mt-3 text-sm leading-7 text-slate-700">{t.closing}</p>
              <p className="mt-4 text-sm text-slate-700">{t.regards}</p>
              <p className="mt-2 font-serif font-bold text-blue-950">{t.team}</p>
              <p className="mt-1 text-sm italic text-amber-700">VEARN — {t.motto}</p>
              <p className="mt-3 text-xs text-slate-500">{t.support}</p>
              <p className="mt-1 text-xs text-slate-500">{t.website}: {window.location.origin}</p>
            </section>

            <section className="partner-letter-section mt-7 border-t border-slate-200 pt-6">
              <h2 className="font-serif text-lg font-bold uppercase tracking-wide text-blue-950">{t.acceptanceTitle}</h2>
              <p className="mt-3 text-sm leading-7 text-slate-700">{t.acceptance}</p>
              <div className="mt-4 grid grid-cols-1 gap-2 border-y border-slate-200 py-4 text-sm sm:grid-cols-2">
                <p><span className="font-semibold text-slate-500">{t.nameLabel}:</span> {member?.name || "—"}</p>
                <p><span className="font-semibold text-slate-500">{t.idLabel}:</span> <span className="font-mono font-semibold">{member?.partner_id || "—"}</span></p>
                <p className="sm:col-span-2"><span className="font-semibold text-slate-500">{t.dateLabel}:</span> {acceptedDate}</p>
              </div>
              {required ? (
                <div className="partner-welcome-accept-controls mt-5">
                  {readOnly && <p role="status" className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-900">
                    Acceptance is disabled in admin member preview. Sign in with this Partner’s own account to accept the letter and open the dashboard.
                  </p>}
                  <p className={`mb-3 text-xs font-semibold ${readToEnd ? "text-emerald-700" : "text-amber-700"}`}>{readToEnd ? t.statusPending : t.scrollHint}</p>
                  <label className={`flex items-start gap-3 rounded-xl border p-4 text-sm leading-6 ${readToEnd && !readOnly ? "border-blue-200 bg-blue-50 text-slate-700" : "border-slate-200 bg-slate-50 text-slate-400"}`}>
                    <input data-testid="welcome-letter-consent" type="checkbox" disabled={!readToEnd || readOnly} checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} className="mt-1 h-4 w-4 accent-blue-700" />
                    <span>{t.readConfirm}</span>
                  </label>
                  <button data-testid="welcome-letter-accept" type="button" disabled={readOnly || !readToEnd || !confirmed || accepting} onClick={() => onAccept(language)} className="mt-4 h-12 w-full rounded-full bg-blue-900 px-6 text-sm font-bold text-white shadow-lg transition hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-35 sm:w-auto">
                  {accepting ? "Saving acceptance…" : t.acceptButton}
                  </button>
                </div>
              ) : (
                <p className="mt-4 rounded-lg bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800">{t.acceptedLabel}: {acceptedDate} · {languageNames[acceptedLanguage] || languageNames.en}</p>
              )}
            </section>

            <footer className="mt-7 border-t border-slate-200 pt-4 text-center text-[11px] leading-5 text-slate-500">
              <p>{t.printNote}</p>
              <p className="mt-2 font-semibold tracking-wide text-blue-900">VEARN · {member?.partner_id || ""} · {member?.name || ""}</p>
            </footer>
          </div>
        </article>
      </div>
    </>
  );
}
